import React from 'react';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
  lines?: number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'text',
  width,
  height,
  lines = 1,
  className = '',
  ...props
}) => {
  const baseClasses = 'animate-pulse bg-secondary-200';

  const variantClasses: Record<string, string> = {
    text: 'rounded',
    circular: 'rounded-full',
    rectangular: 'rounded-lg',
  };

  const defaultSizes: Record<string, { width: string; height: string }> = {
    text: { width: '100%', height: '1rem' },
    circular: { width: '40px', height: '40px' },
    rectangular: { width: '100%', height: '100px' },
  };

  const style: React.CSSProperties = {
    width: width || defaultSizes[variant].width,
    height: height || defaultSizes[variant].height,
  };

  const classes = [baseClasses, variantClasses[variant], className]
    .filter(Boolean)
    .join(' ');

  if (variant === 'text' && lines > 1) {
    return (
      <div className="space-y-2" {...props}>
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={classes}
            style={{
              ...style,
              width: index === lines - 1 ? '60%' : style.width,
            }}
          />
        ))}
      </div>
    );
  }

  return <div className={classes} style={style} {...props} />;
};

// Pre-built skeleton patterns
export const CardSkeleton: React.FC<{ rows?: number }> = ({ rows = 3 }) => (
  <div className="p-4 space-y-3">
    <Skeleton variant="text" height="1.5rem" width="70%" />
    {Array.from({ length: rows }).map((_, index) => (
      <Skeleton
        key={index}
        variant="text"
        width={`${Math.random() * 30 + 60}%`}
      />
    ))}
    <Skeleton variant="rectangular" height="100px" className="mt-4" />
  </div>
);

export const ListSkeleton: React.FC<{
  rows?: number;
  showAvatar?: boolean;
}> = ({ rows = 5, showAvatar = false }) => (
  <div className="divide-y divide-secondary-200">
    {Array.from({ length: rows }).map((_, index) => (
      <div key={index} className="flex items-center py-3 px-4">
        {showAvatar && (
          <Skeleton variant="circular" className="mr-3 flex-shrink-0" />
        )}
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="70%" />
          <Skeleton variant="text" width="40%" height="0.75rem" />
        </div>
        <Skeleton
          variant="rectangular"
          width="80px"
          height="24px"
          className="flex-shrink-0"
        />
      </div>
    ))}
  </div>
);

export const TableSkeleton: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => (
  <div>
    <div className="flex p-3 bg-secondary-50 border-b border-secondary-200">
      {Array.from({ length: columns }).map((_, index) => (
        <div
          key={index}
          className={`flex-1 ${index < columns - 1 ? 'mr-4' : ''}`}
        >
          <Skeleton variant="text" width="60%" height="0.875rem" />
        </div>
      ))}
    </div>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <div key={rowIndex} className="flex p-3 border-b border-secondary-200">
        {Array.from({ length: columns }).map((_, colIndex) => (
          <div
            key={colIndex}
            className={`flex-1 ${colIndex < columns - 1 ? 'mr-4' : ''}`}
          >
            <Skeleton
              variant="text"
              width={colIndex === columns - 1 ? '40%' : '80%'}
              height="0.875rem"
            />
          </div>
        ))}
      </div>
    ))}
  </div>
);

export default Skeleton;
